import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_SignatureBackgroundRemover = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/signature-background-remover',
			name: 'Signature PNG — Remove Background from Signature | Lift',
			description:
				'Extract signature from image instantly with AI. No watermark. Make signature transparent, create PNG signature, remove signature background in seconds. Try now!',
			url: 'https://liftapp.ai/signature-background-remover',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/signature-background-remover#product' },
				{ '@id': 'https://liftapp.ai/signature-background-remover#howto' },
				{ '@id': 'https://liftapp.ai/signature-background-remover#benefits' },
				{ '@id': 'https://liftapp.ai/signature-background-remover#faq' },
				{ '@id': 'https://liftapp.ai/signature-background-remover#why-lift' },
				{
					'@id':
						'https://liftapp.ai/signature-background-remover#explore-tools',
				},
				{ '@id': 'https://liftapp.ai/signature-background-remover#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/signature-background-remover#product',
			name: 'Signature Background Remover Online',
			description:
				'Remove background from signature and create transparent PNG in seconds. Extract signature from image, and convert to PNG, no watermarks. Try Lift Signature Extractor now.',
			url: 'https://liftapp.ai/signature-background-remover',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/signature-background-remover#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift extracts your signature and removes the background automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/signature-background-remover#benefits',
			name: 'Who can benefit from Signature Background Remover?',
			description:
				"Lift's AI Signature Background Remover is useful for professionals, creators, and businesses who want clean, transparent signature PNG images for documents, contracts, branding, or digital use.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'E-commerce Sellers — Remove background noise from handwritten signatures, logos, or branded stamps and overlay them neatly on product images or promotional materials.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content Creators — Add authentic signature overlays to artwork, certificates, and digital content. Extract signature from photos instantly to create personalized branding elements for social media posts and YouTube videos.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Designers & Freelancers — Speed up document design with clean PNG signatures ready for client contracts and mockups. Remove signature background in bulk to build reusable signature libraries for branding projects.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small Business Owners — Digitize your signature for contracts, quotes, and official correspondence without scanning equipment. Make signature transparent for professional letterheads, email signatures, and marketing materials.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & Educators — Create transparent signatures for digital assignments, permission forms, and academic certificates. Remove white background from signature scans to make professional-looking documents and portfolios.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Extract signature',
				description:
					'Example of how Lift Signature Background Remover extracts signatures and creates transparent PNG images for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/signature-background-remover#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Signature Background Remover?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Signature Background Remover is a premium online tool that instantly extracts signature from image and removes background with pixel-perfect precision — ideal for contracts, documents, certificates, or digital branding.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Signature Extractor work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your signature photo to Lift's signature background remover tool. Our AI automatically detects the signature and removes the background in seconds — no photo editing experience needed. Download up to 20 transparent signature PNGs with invisible backgrounds.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Signature Background Remover uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/signature-background-remover#why-lift',
			name: "Why Lift's AI Signature Background Remover stands out",
			description:
				"Lift's Signature Background Remover combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Sign background remover',
				description:
					"Comparison of Lift's AI Signature Background Remover benefits — professional quality, AI accuracy, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/signature-background-remover#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/signature-background-remover#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Signature Background Remover',
					item: 'https://liftapp.ai/signature-background-remover',
				},
			],
		},
	],
};
